// warm: have the main rooms ready before anyone asks for them.
//
// lib/os/usage.ts warms what a person has opened before. That leaves a brand-new
// account, the one most worth impressing, with nothing warmed at all: every
// first press on the navigator waited on a download, and the stage stood empty
// while it came. The main rooms are small and certain to be visited, so they are
// loaded for everyone, one per idle slot, after the dashboard has settled, and
// never on a connection that asked us to save data.

type Loader = () => Promise<unknown>;

export interface WarmEnv {
  /** run `cb` when the main thread is idle. */
  idle(cb: () => void): void;
  /** true when the connection asked to spare data, or is too slow to spend it. */
  frugal(): boolean;
  hidden(): boolean;
}

export const browserWarmEnv: WarmEnv = {
  idle(cb) {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
    if (w.requestIdleCallback) w.requestIdleCallback(cb, { timeout: 4000 });
    else setTimeout(cb, 400);
  },
  frugal() {
    const c = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    return !!c && (c.saveData === true || c.effectiveType === "slow-2g" || c.effectiveType === "2g");
  },
  hidden: () => document.hidden,
};

/** Load `order` (keys of `loaders`) one at a time, each in its own idle slot.
 *  A loader that fails is skipped; it will load on use. Returns a cancel. */
export function warmInOrder(loaders: Record<string, Loader>, order: readonly string[], env: WarmEnv = browserWarmEnv): () => void {
  let cancelled = false;
  const queue = order.filter((k) => typeof loaders[k] === "function");
  const next = () => {
    if (cancelled) return;
    // a spare-data connection, or a tab nobody is looking at, warms nothing
    if (env.frugal()) return;
    if (env.hidden()) return;
    const key = queue.shift();
    if (key === undefined) return;
    loaders[key]().catch(() => {
      /* a failed warm just means it loads when it is opened */
    });
    env.idle(next);
  };
  env.idle(next);
  return () => {
    cancelled = true;
  };
}
